

import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Chip,
    Box,
    Stack,
    Button,
    FormHelperText,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    QuizOutlined as QuizOutlinedIcon,
    LinkOutlined as LinkOutlinedIcon,
    ForumOutlined as ForumOutlinedIcon,
} from '@mui/icons-material';
import JsonExportConfirmModal from '../../Component/Modal/JsonExportConfirmModal';
import { AuthContext } from '../../context/auth-context';
import { OVERVIEW_LLM_LABELS } from '../../utils/constant';

const isSearchEnabledLLM = (llmValue) => /search/i.test(OVERVIEW_LLM_LABELS[llmValue] || llmValue || '');

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export default function SummaryReportModal({
    open,
    onClose,
    onGenerate,
    onGenerateJson,
    loading = false,
    jsonLoading = false,
    brandName,
    categories = [],
    llms = [],
    markets = [],
    persona = [],
    title,
    hideGenerateButton = false,
    reportTypeTabs = null, // [{label, flag}] — when provided, shown as tabs above inputs
    flag = 'brand_audit',
    type = 'brand',
    availableMetricKeys = null,
}) {
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedLLMs, setSelectedLLMs] = useState([]);
    const [selectedMarkets, setSelectedMarkets] = useState([]);
    const [selectedPersona, setSelectedPersona] = useState([]);
    const [errors, setErrors] = useState({});
    const [jsonConfirmOpen, setJsonConfirmOpen] = useState(false);
    const [pendingSelections, setPendingSelections] = useState(null);
    const [selectedReportTypeFlag, setSelectedReportTypeFlag] = useState('');

    const { user } = useContext(AuthContext);

    // Initialize with default values if only one option exists
    useEffect(() => {
        if (open) {
            setSelectedCategory(categories.length === 1 ? categories[0] : '');
            setSelectedLLMs(llms.length === 1 ? llms : []);
            setSelectedMarkets(markets.length === 1 ? markets : []);
            setSelectedPersona(persona.length === 1 ? persona : []);
            setErrors({});
            // First tab that is not disabled (the first tab may be disabled for some users).
            setSelectedReportTypeFlag((reportTypeTabs?.find((rt) => !rt.disabled) || reportTypeTabs?.[0])?.flag || '');
        }
    }, [open, categories, llms, markets, persona]);

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
        if (errors.category) {
            setErrors((prev) => ({ ...prev, category: false }));
        }
    };

    const handleLLMChange = (e) => {
        const { value } = e.target;
        setSelectedLLMs(typeof value === 'string' ? value.split(',') : value);
        if (errors.llms) {
            setErrors((prev) => ({ ...prev, llms: false }));
        }
    };

    const handleMarketChange = (e) => {
        const { value } = e.target;
        setSelectedMarkets(typeof value === 'string' ? value.split(',') : value);
        if (errors.markets) {
            setErrors((prev) => ({ ...prev, markets: false }));
        }
    };

    const handlePersonaChange = (e) => {
        const { value } = e.target;
        setSelectedPersona(typeof value === 'string' ? value.split(',') : value);
        if (errors.persona) {
            setErrors((prev) => ({ ...prev, persona: false }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!selectedCategory) {
            newErrors.category = true;
        }
        if (selectedLLMs.length === 0) {
            newErrors.llms = true;
        }
        if (selectedMarkets.length === 0) {
            newErrors.markets = true;
        }

        if (selectedPersona.length === 0) {
            newErrors.persona = true;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildSelections = () => ({
        category: selectedCategory,
        llms: selectedLLMs,
        markets: selectedMarkets,
        persona: selectedPersona,
        ...(reportTypeTabs && { reportType: selectedReportTypeFlag }),
    });

    const handleGenerate = () => {
        if (validateForm()) {
            onGenerate(buildSelections());
        }
    };

    const handleGenerateJson = () => {
        if (validateForm()) {
            setPendingSelections(buildSelections());
            setJsonConfirmOpen(true);
        }
    };

    const handleJsonConfirmClose = () => {
        if (jsonLoading) return;
        setJsonConfirmOpen(false);
        setPendingSelections(null);
    };

    const handleConfirmedJsonGenerate = (exportSelections) => {
        if (!pendingSelections) return;
        setJsonConfirmOpen(false);
        onGenerateJson({ ...pendingSelections, ...exportSelections });
    };

    const hasSearchLLM = selectedLLMs.some(isSearchEnabledLLM);
    const hasOfflineLLM = selectedLLMs.some((llm) => !isSearchEnabledLLM(llm));
    const showIncludeSources = !(hasOfflineLLM && !hasSearchLLM);
    const includeSourcesNote = hasOfflineLLM && hasSearchLLM
        ? 'Note: sources are only available for search-enabled LLM models — offline models in this selection will not contribute any source data.'
        : null;

    const jsonExportOptions = useMemo(() => [
        ...((!availableMetricKeys || availableMetricKeys.includes('includeSentiment')) ? [{
            key: 'includeSentimentQuestions',
            icon: QuizOutlinedIcon,
            title: 'Include Sentiment Questions',
            description: 'Includes all brand sentiment questions and their LLM-generated responses across every tracked brand.',
            badge: 'LARGE DATASET',
        }] : []),
        ...(showIncludeSources ? [{
            key: 'includeSources',
            icon: LinkOutlinedIcon,
            title: 'Include Sources',
            description: 'Includes full source URLs, source counts, classifications (Own, Competitors, PR Media, Third Party), and LLM-wise source breakdown.',
            note: includeSourcesNote,
            badge: 'LARGE DATASET',
        }] : []),
        ...(flag === 'brand_audit' ? [{
            key: 'includeConversation',
            icon: ForumOutlinedIcon,
            title: 'Include Conversations',
            description: 'Includes all conversation insights for the brand and category.',
            badge: 'LARGE DATASET',
        }] : []),
    ], [flag, availableMetricKeys, showIncludeSources, includeSourcesNote]);

    const handleRemoveLLM = (llm) => {
        setSelectedLLMs((prev) => prev.filter((item) => item !== llm));
    };

    const handleRemoveMarket = (market) => {
        setSelectedMarkets((prev) => prev.filter((item) => item !== market));
    };

    const handleRemovePersona = (persona) => {
        setSelectedPersona((prev) => prev.filter((item) => item !== persona));
    };

    return (
        <>
        <JsonExportConfirmModal
            open={jsonConfirmOpen}
            onClose={handleJsonConfirmClose}
            onConfirm={handleConfirmedJsonGenerate}
            loading={jsonLoading}
            options={jsonExportOptions}
            type={type}
            showMetrics
            availableMetricKeys={availableMetricKeys}
            allowFullExport
            moduleKey={flag === 'brand_audit' ? 'brandAudit' : 'categoryAnalysis'}
        />

        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {title || "Generate Summary Report"}{brandName && ` - ${brandName}`}
            </DialogTitle>
            <DialogContent>
                {reportTypeTabs && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Report Type *
                        </Typography>
                        <ToggleButtonGroup
                            value={selectedReportTypeFlag}
                            exclusive
                            onChange={(_, val) => { if (val) setSelectedReportTypeFlag(val); }}
                            size="small"
                        >
                            {reportTypeTabs.map((rt) => (
                                <ToggleButton key={rt.flag} value={rt.flag} disabled={!!rt.disabled}>
                                    {rt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        {reportTypeTabs.find(rt => rt.flag === selectedReportTypeFlag)?.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                                {reportTypeTabs.find(rt => rt.flag === selectedReportTypeFlag).description}
                            </Typography>
                        )}
                    </Box>
                )}
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* Category Selection - Single Select */}
                    <FormControl fullWidth error={errors.category}>
                        <InputLabel>Category *</InputLabel>
                        <Select
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            label="Category *"
                        >
                            <MenuItem value="">
                                <em>Select a category</em>
                            </MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                    {category}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.category && (
                            <FormHelperText>Please select a category</FormHelperText>
                        )}
                    </FormControl>

                    {/* LLM Selection - Multi-Select Dropdown */}
                    <FormControl fullWidth error={errors.llms}>
                        <InputLabel>LLMs *</InputLabel>
                        <Select
                            multiple
                            value={selectedLLMs}
                            onChange={handleLLMChange}
                            input={<OutlinedInput label="LLMs *" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={value}
                                            onDelete={() => handleRemoveLLM(value)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        />
                                    ))}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            {llms.map((llm) => (
                                <MenuItem key={llm} value={llm}>
                                    {llm}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.llms && (
                            <FormHelperText>Please select at least one LLM</FormHelperText>
                        )}
                    </FormControl>

                    {/* Market Selection - Multi-Select Dropdown */}
                    <FormControl fullWidth error={errors.markets}>
                        <InputLabel>Markets *</InputLabel>
                        <Select
                            multiple
                            value={selectedMarkets}
                            onChange={handleMarketChange}
                            input={<OutlinedInput label="Markets *" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={value}
                                            onDelete={() => handleRemoveMarket(value)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        />
                                    ))}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            {markets.map((market) => (
                                <MenuItem key={market} value={market}>
                                    {market}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.markets && (
                            <FormHelperText>Please select at least one market</FormHelperText>
                        )}
                    </FormControl>

                    {/* Persona Selection - Multi-Select Dropdown */}
                    <FormControl fullWidth error={errors.persona}>
                        <InputLabel>Persona *</InputLabel>
                        <Select
                            multiple
                            value={selectedPersona}
                            onChange={handlePersonaChange}
                            input={<OutlinedInput label="Persona *" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={value}
                                            onDelete={() => handleRemovePersona(value)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        />
                                    ))}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            {persona.map((p) => (
                                <MenuItem key={p} value={p}>
                                    {p}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.persona && (
                            <FormHelperText>Please select at least one persona</FormHelperText>
                        )}
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={loading || jsonLoading}>
                    Cancel
                </Button>
                {user?.is_json_export_enabled !== false && (<Button
                    onClick={handleGenerateJson}
                    variant="outlined"
                    color="primary"
                    disabled={loading || jsonLoading}
                >
                    {jsonLoading ? 'Downloading…' : 'Generate JSON'}
                </Button>)}

                {!hideGenerateButton && (
                    <Button
                        onClick={handleGenerate}
                        variant="contained"
                        color="primary"
                        disabled={loading || jsonLoading}
                    >
                        {loading ? 'Generating…' : 'Generate'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
        </>
    );
}
